"use client";

import { cn } from "@repo/ui/utils";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselDots,
  CarouselNext,
} from "@repo/ui/carousel";
import { Icon } from "@repo/ui/icon";
import { Button } from "@repo/ui/button";
import { Tag } from "@repo/ui/tag";
import { VisualWrapper } from "@/components/common/visual/visual.component";
import { ActionLink } from "@/components/common/actions/actions.component";
import { getI18nString } from "@/lib/utils";
import type { ActionType, VisualType } from "@/lib/types";
import type { ApartmentRef } from "./apartment-card.types";
import type { IconName } from "@repo/ui/icon";

interface ApartmentCardSingleProps {
  apartment: ApartmentRef;
  className?: string;
}

export function ApartmentCardSingle({
  apartment,
  className,
}: ApartmentCardSingleProps) {
  const content = apartment.apartmentListingContent;
  const images = (content?.visual?.mediaSource ?? []).map(
    (source) => ({ mediaSource: source }) as VisualType,
  );
  const hasMultipleImages = images.length > 1;
  const title = (content ? getI18nString(content.headline) : undefined) || "";
  const highlights = content?.highlights ?? [];
  const enquireNow = apartment.enquireNow as ActionType | null;

  return (
    <div
      data-testid="apartment-card-single"
      className={cn("flex w-full flex-col gap-6 tablet:gap-7", className)}
    >
      {/* Image carousel */}
      <div className="relative w-full overflow-clip rounded-md">
        {images.length > 0 ? (
          <Carousel loop={hasMultipleImages}>
            <CarouselContent>
              {images.map((image, index) => (
                <CarouselItem key={index}>
                  <div className="relative w-full aspect-[755/429]">
                    <VisualWrapper
                      visual={image}
                      asBackground
                      className="size-full"
                      visualClassName="size-full"
                      imgClassName="object-cover"
                      extraImageProps={{
                        sizes:
                          "(max-width: 768px) 100vw, (max-width: 1024px) 100vw, 755px",
                        cropAspect: 755 / 429,
                      }}
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            {hasMultipleImages && (
              <>
                <CarouselNext className="absolute right-4 top-1/2 -translate-y-1/2 shadow-md" />
                <CarouselDots
                  className="absolute bottom-4 left-1/2 -translate-x-1/2"
                  dotClassName="bg-white/40"
                  activeDotClassName="bg-white"
                />
              </>
            )}
          </Carousel>
        ) : (
          <div className="flex w-full items-center justify-center bg-surface-base-secondary aspect-[755/429]">
            <Icon name="image" className="size-12 text-text-disabled" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex w-full flex-col gap-4">
        {/* Title and Tags */}
        <div className="flex w-full flex-col gap-3">
          {apartment.availabilityTag && (
            <Tag variant="light" size="md" className="w-fit">
              {apartment.availabilityTag}
            </Tag>
          )}
          <h3 className="typography-mobile-heading-subtitle-1 text-text-primary tablet:typography-desktop-heading-subtitle-1">
            {title}
          </h3>
          {apartment.price && (
            <p className="typography-component-xs-medium text-text-secondary">
              {apartment.price}
            </p>
          )}
        </div>

        {/* Highlights list */}
        {highlights.length > 0 && (
          <ul className="flex w-full flex-col gap-4 mt-2">
            {highlights.slice(0, 4).map((highlight) => (
              <li key={highlight._id} className="flex items-center gap-3 py-2">
                {highlight.icon && (
                  <Icon
                    name={highlight.icon as IconName}
                    className="size-6 shrink-0 text-icon-primary"
                  />
                )}
                <span className="typography-body-xs-medium text-text-primary">
                  {getI18nString(highlight.headline, "")}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
      {/* CTA Buttons */}
      {enquireNow?.to && (
        <div className="flex flex-wrap gap-4">
          <ActionLink actionTo={enquireNow.to}>
            <Button size="sm" variant="blue-filled" asChild>
              <span>{getI18nString(enquireNow.text, "Enquire Now")}</span>
            </Button>
          </ActionLink>
        </div>
      )}
    </div>
  );
}
